import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for user interaction API path
 */
export const USER_INTERACTION_API_PATH = new InjectionToken<string>('USER_INTERACTION_API_PATH');

/**
 * User Interaction API Service for handling user interaction-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class UserInteractionApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(USER_INTERACTION_API_PATH) private userInteractionApiPath: string,
  ) {}

  /**
   * Add a bookmark for a user
   * @param payload Bookmark data (e.g., { itemId: string, itemType: string })
   * @returns Promise with bookmark response
   */
  async addBookmark(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  }

  /**
   * Remove a bookmark for a user
   * @param payload Bookmark data (e.g., { itemId: string, itemType: string })
   * @returns Promise with bookmark removal response
   */
  async removeBookmark(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark`;

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  }

  /**
   * Get bookmarks for a user
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with bookmarks list
   */
  async getBookmarks(page: number = 1, size: number = 10): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark?page=${page}&size=${size}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw error;
    }
  }

  /**
   * Add a like for an item
   * @param payload Like data (e.g., { itemId: string, itemType: string })
   * @returns Promise with like response
   */
  async addLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error adding like:', error);
      throw error;
    }
  }

  /**
   * Remove a like for an item
   * @param payload Like data (e.g., { itemId: string, itemType: string })
   * @returns Promise with like removal response
   */
  async removeLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like`;

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error removing like:', error);
      throw error;
    }
  }

  /**
   * Get likes for an item
   * @param itemId Item ID
   * @param itemType Item type (e.g., 'dataset', 'model', 'usecase')
   * @returns Promise with likes data
   */
  async getLikes(itemId: string, itemType: string): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like?itemId=${itemId}&itemType=${itemType}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching likes:', error);
      throw error;
    }
  }

  /**
   * Get user interactions
   * @param queryParams Query parameters object (e.g., { page: 1, size: 10, type: 'bookmark' })
   * @returns Promise with user interactions data
   */
  async userInteractions(queryParams?: Record<string, any>): Promise<any> {
    try {
        // https://v2.dev.controlplane.iudx.io/iudx/v2/user/interactions
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/interactions`;

      // Merge with default pagination: size=6 (override if caller provides size)
      const mergedParams: Record<string, unknown> = { size: 6, ...queryParams };

      // Build query string from merged params
      if (mergedParams && Object.keys(mergedParams).length > 0) {
        const params = new URLSearchParams();
        Object.keys(mergedParams).forEach((key) => {
          const value = mergedParams[key];
          if (value !== null && value !== undefined && value !== '') {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching user interactions:', error);
      throw error;
    }
  }

  /**
   * Create a bookmark or like for an item
   * @param payload Interaction data (e.g., { itemId: string, itemType: string, type: 'bookmark' | 'like' })
   * @returns Promise with interaction response
   */
  async createBookmarkOrLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/interactions`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error creating bookmark or like:', error);
      throw error;
    }
  }

  /**
   * Get user feedback (GET /iudx/v2/user/feedback).
   * @param queryParams Optional filters (assetId, userId, actionSubtype, page, size)
   * @returns Promise with paginated feedback response
   */
  async getUserFeedback(queryParams?: Record<string, any>): Promise<any> {
    try {
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback`;
      const mergedParams: Record<string, unknown> = { page: 1, size: 10, ...queryParams };

      if (Object.keys(mergedParams).length > 0) {
        const params = new URLSearchParams();
        Object.keys(mergedParams).forEach((key) => {
          const value = mergedParams[key];
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== null && item !== undefined && item !== '') {
                  params.append(key, String(item));
                }
              });
            } else {
              params.append(key, String(value));
            }
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await this.networkService.get_api(
        url,
        undefined,
        true, // disableToaster — page-load fetch should not interrupt the view
      );

      return response;
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      throw error;
    }
  }

  /**
   * Approved feedback visible on the catalogue (GET /iudx/v2/user/feedback/approved).
   * @see https://v2.dev.iudx.io/controlplane/apis#tag/User-Interactions/operation/get-approved-user-feedback
   */
  async getApprovedUserFeedback(queryParams?: {
    assetId?: string;
    userId?: string;
    page?: number;
    size?: number;
    /** Star filter, 1–5. Repeatable on the API; the dataset page sends one value. */
    rating?: number;
    /** `ratingCreatedAt:desc` (new to old) or `ratingCreatedAt:asc` (old to new). */
    sort?: string;
  }): Promise<any> {
    try {
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback/approved`;
      const mergedParams: Record<string, unknown> = { page: 1, size: 100, ...queryParams };
      const params = new URLSearchParams();
      Object.keys(mergedParams).forEach((key) => {
        const value = mergedParams[key];
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return await this.networkService.get_api(url, undefined, true);
    } catch (error) {
      console.error('Error fetching approved user feedback:', error);
      throw error;
    }
  }

  /**
   * Get platform-wide feedback for admin (GET /iudx/v2/user/feedback/platform).
   * @param queryParams Optional filters (page, size, startDate, endDate, etc.)
   * @returns Promise with summary, data[], and paginationInfo
   */
  async getPlatformFeedback(queryParams?: Record<string, any>): Promise<any> {
    try {
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback/platform`;
      const mergedParams: Record<string, unknown> = { page: 1, size: 10, ...queryParams };

      if (Object.keys(mergedParams).length > 0) {
        const params = new URLSearchParams();
        Object.keys(mergedParams).forEach((key) => {
          const value = mergedParams[key];
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== null && item !== undefined && item !== '') {
                  params.append(key, String(item));
                }
              });
            } else {
              params.append(key, String(value));
            }
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await this.networkService.get_api(
        url,
        undefined,
        true, // disableToaster — page-load fetch should not interrupt the view
      );

      return response;
    } catch (error) {
      console.error('Error fetching platform feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback submitted by the authenticated consumer (GET /iudx/v2/user/feedback/user).
   * User identity is derived from the Bearer token — do not pass userId.
   * @see https://v2.dev.iudx.io/controlplane/apis#tag/User-Interactions/operation/get-my-user-feedback
   */
  async getMyUserFeedback(queryParams?: Record<string, any>): Promise<any> {
    try {
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback/user`;
      const mergedParams: Record<string, unknown> = { page: 1, size: 10, ...queryParams };

      if (Object.keys(mergedParams).length > 0) {
        const params = new URLSearchParams();
        Object.keys(mergedParams).forEach((key) => {
          const value = mergedParams[key];
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== null && item !== undefined && item !== '') {
                  params.append(key, String(item));
                }
              });
            } else {
              params.append(key, String(value));
            }
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await this.networkService.get_api(
        url,
        undefined,
        true, // disableToaster — page-load fetch should not interrupt the view
      );

      return response;
    } catch (error) {
      console.error('Error fetching my user feedback:', error);
      throw error;
    }
  }

  /**
   * Submit user feedback for an asset (POST /iudx/v2/user/feedback).
   * @param payload { assetId, assetType, entityRating?, actionSubtype?, actionSubdata? }
   * @returns Promise with feedback response
   */
  async postUserFeedback(payload: {
    assetId: string;
    assetType: 'DATABANK' | 'AI_MODEL' | 'USECASE';
    entityRating?: number;
    actionSubtype?: string;
    actionSubdata?: Record<string, unknown>;
  }): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error posting user feedback:', error);
      throw error;
    }
  }

  /**
   * Approve or reject user feedback (PUT /iudx/v2/user/feedback).
   */
  async updateUserFeedbackStatus(payload: {
    id: string;
    status: 'APPROVED' | 'REJECTED';
  }): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback`;
      return await this.networkService.put_api(url, payload);
    } catch (error) {
      console.error('Error updating user feedback status:', error);
      throw error;
    }
  }

  /**
   * Approve or reject pending platform feedback (PUT /iudx/v2/user/feedback/{id}).
   * COS Admin only. `comment` is required when status is REJECTED.
   * @see https://v2.dev.iudx.io/controlplane/apis#tag/User-Interactions/operation/put-platform-user-feedback
   */
  async updatePlatformFeedbackStatus(payload: {
    id: string;
    status: 'APPROVED' | 'REJECTED';
    comment?: string;
  }): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback/${payload.id}`;
      const body: { status: 'APPROVED' | 'REJECTED'; comment?: string } = {
        status: payload.status,
      };
      const comment = payload.comment?.trim();
      if (comment) {
        body.comment = comment;
      }
      return await this.networkService.put_api(url, body);
    } catch (error) {
      console.error('Error updating platform feedback status:', error);
      throw error;
    }
  }

  /**
   * Update pending user feedback (PUT /iudx/v2/user/feedback).
   * Requires assetId + assetType. At least one of entityRating or actionSubtype/actionSubdata.
   * actionSubtype and actionSubdata must be provided together.
   * @see https://v2.dev.iudx.io/controlplane/apis#tag/User-Interactions/operation/put-user-feedback
   */
  async updateUserFeedback(payload: {
    assetId: string;
    assetType: 'DATABANK' | 'AI_MODEL' | 'USECASE' | string;
    entityRating?: number;
    actionSubtype?: string;
    actionSubdata?: Record<string, unknown>;
  }): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback`;
      const body: Record<string, unknown> = {
        assetId: payload.assetId,
        assetType: payload.assetType,
      };
      if (payload.entityRating != null) {
        body['entityRating'] = payload.entityRating;
      }
      if (payload.actionSubtype && payload.actionSubdata) {
        body['actionSubtype'] = payload.actionSubtype;
        body['actionSubdata'] = payload.actionSubdata;
      }
      return await this.networkService.put_api(url, body);
    } catch (error) {
      console.error('Error updating user feedback:', error);
      throw error;
    }
  }

  /**
   * Clear the authenticated user's feedback on an asset
   * (DELETE /iudx/v2/user/feedback?assetId=).
   * @see https://v2.dev.iudx.io/controlplane/apis#tag/User-Interactions/operation/delete-user-feedback
   */
  async deleteUserFeedback(payload: { assetId: string }): Promise<any> {
    try {
      const assetId = payload.assetId.trim();
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/feedback?assetId=${encodeURIComponent(assetId)}`;
      return await this.networkService.delete_api(url);
    } catch (error) {
      console.error('Error deleting user feedback:', error);
      throw error;
    }
  }
}
